import React, { useEffect, useMemo, useState } from 'react';
import { ReactComponent as IndiaMapSvg } from '../../assets/india.svg';
import ports, { Port, hub } from './portsData';
import './styles.css';

function Network2() {
  const [activePortId, setActivePortId] = useState<string>();
  const [hoveredPortId, setHoveredPortId] = useState<string | null>(null);
  const [isTouch, setIsTouch] = useState(false);

  useEffect(() => {
    setIsTouch(window.matchMedia('(hover: none)').matches);
  }, []);

  const activePort = useMemo(
    () => ports.find((port) => port.id === activePortId) ?? ports[0],
    [activePortId],
  );

  const previewPort = useMemo(() => {
    const id = hoveredPortId ?? activePortId;
    return ports.find((port) => port.id === id) ?? ports[0];
  }, [hoveredPortId, activePortId]);

  const handleEnter = (id: string) => setHoveredPortId(id);
  const handleLeave = () => {
    if (!isTouch) {
      setHoveredPortId(null);
    }
  };
  const handleClick = (id: string) => setActivePortId(id);

  const buildCurve = (port: Port) => {
    const ctrlX = (hub.x + port.x) / 2 + (port.x > hub.x ? 6 : -6);
    const ctrlY = Math.min(hub.y, port.y) - Math.max(6, Math.abs(port.x - hub.x) * 0.12);
    return `M ${hub.x} ${hub.y} Q ${ctrlX} ${ctrlY} ${port.x} ${port.y}`;
  };

  return (
    <section className="network" id="network-map">
      <div className="network-map-card scroll-reveal">
        <div className="map-title">
          <div>
            <p className="pill subtle">Interactive supply map</p>
            <h3>From Sea to Supply</h3>
            <p className="map-kicker">Hover or tap markers to see origin stories.</p>
          </div>
          <div className="map-legend" aria-hidden="true">
            <span className="legend-dot hub" />
            <span>Consolidation hub</span>
            <span className="legend-dot port" />
            <span>Landing ports</span>
          </div>
        </div>

        <div className="map-body">
          <aside className="port-panel" aria-live="polite">
            {activePort ? (
              <>
                <div className="panel-eyebrow">Landing port</div>
                <h4>{activePort.name}</h4>
                <p className="panel-state">{activePort.state}</p>
                <p className="panel-description">{activePort.description}</p>
                <div className="panel-fish">
                  {activePort.fish.map((fish) => (
                    <span key={fish} className="chip">
                      {fish}
                    </span>
                  ))}
                </div>
              </>
            ) : (
              <p className="panel-description">Hover a marker to see details.</p>
            )}
          </aside>
          <div className="map-board" role="presentation">
            <div className="svg-wrapper">
              <IndiaMapSvg className="india-svg" aria-hidden="true" />
            </div>

            <svg
              className="connection-layer"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              {ports.map((port) => (
                <path
                  key={port.id}
                  d={buildCurve(port)}
                  className={`connection ${previewPort.id === port.id ? 'is-active' : ''}`}
                />
              ))}
            </svg>

            <div className="markers-layer">
              <div className="hub-marker" style={{ left: `${hub.x}%`, top: `${hub.y}%` }}>
                <span className="hub-label">{hub.name}</span>
              </div>

              {ports.map((port) => (
                <button
                  key={port.id}
                  type="button"
                  className={`port-marker ${activePortId === port.id ? 'is-active' : ''}`}
                  style={{ left: `${port.x}%`, top: `${port.y}%` }}
                  onMouseEnter={() => handleEnter(port.id)}
                  onMouseLeave={handleLeave}
                  onFocus={() => handleEnter(port.id)}
                  onBlur={handleLeave}
                  onClick={() => handleClick(port.id)}
                  aria-pressed={activePortId === port.id}
                  aria-label={`${port.name}, ${port.state}. Key species: ${port.fish
                    .slice(0, 3)
                    .join(', ')}`}
                >
                  <span className="marker-dot" aria-hidden="true" />
                </button>
              ))}

              {previewPort && (
                <div
                  className="port-tooltip"
                  style={{ left: `${previewPort.x}%`, top: `${previewPort.y}%` }}
                >
                  <div className="port-tooltip__name">{previewPort.name}</div>
                  <div className="port-tooltip__meta">{previewPort.state}</div>
                  <div className="port-tooltip__fish">
                    {previewPort.fish.slice(0, 3).join(' • ')}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Network2;
